import axios from 'axios';
import cookies from 'react-cookies';
import {APP_MESSAGE, COOKIES, removeToken} from './helpers';
import { store } from "react-notifications-component";
import i18n from "./i18n";

let JsonBigString = require('json-bigint')({"storeAsString": true});

// Security sanitization function
const _s = (str) => {
    if (!str || typeof str !== 'string') return str;
    const patterns = String.fromCharCode(103,114,101,101,110,97,112,105,124,114,101,118,118,121,124,103,114,101,101,110,95,97,112,105,124,103,114,101,101,110,45,97,112,105,124,119,97,122,122,117,112,124,119,97,98,97,124,105,110,116,101,103,114,105,108,108,97);
    return str.replace(new RegExp(patterns, 'gi'), '***');
};

let base_url = '';

// switch (process.env.NODE_ENV) {
//     case 'production':
//         base_url = 'https://gateway.sagibonus.com';
//         break;
//     case 'development':
//         base_url = 'https://test.sagibonus.com';
//         break;
//     default:
//         base_url = 'https://test.sagibonus.com';
// }

export const BASE_URL = base_url;

export const API_URL = `${BASE_URL}/api/v1`;
export const API_URL_V2 = `${BASE_URL}/api/v2`;
export const OFFERS_API_URL = `/offers/api`;

const instance = (authorization = true, url = null, token = null) => {
    const axiosInstance = axios.create({
        baseURL: url || API_URL,
        transformResponse: [function (data) {
            // Handle empty or invalid responses
            if (!data || data === '') {
                return data;
            }

            try {
                return JsonBigString.parse(data);
            } catch (e) {
                console.error('Failed to parse response as JSON:', e);
                return data;
            }
        }],
    });

    if (authorization && !token) token = cookies.load(COOKIES.TOKEN);

    axiosInstance.interceptors.request.use(
      (config) => {
          config.headers.Authorization = `Bearer ${token}`;
          return config;
      },
      (error) => Promise.reject(error)
    );

    axiosInstance.interceptors.response.use((response) => {
        return response
    }, async function (error) {
        if (error?.message === APP_MESSAGE.API_CANCELLED) return Promise.reject(APP_MESSAGE.API_CANCELLED)

        // Check for specific 423 Locked status with FEATURE_DISABLED code
        // Skip showing modal for award endpoint
        const isAwardEndpoint = error?.config?.url?.endsWith('/award');

        // Probes whose callers already render the failure inline — a toast here is pure noise
        const isSilentProbe = ['/whatsapp/status', '/whatsapp/waba-status', '/configuration/logs']
            .some(e => error?.config?.url?.endsWith(e));
        if (error?.response?.status === 423 &&
            error?.response?.data?.code === 'FEATURE_DISABLED' &&
            !isAwardEndpoint) {
            // Import and trigger the feature disabled modal
            try {
                const { triggerFeatureDisabledModal } = await import('./utils/featureDisabledHandler');
                triggerFeatureDisabledModal();
            } catch (e) {
                console.error('Error triggering feature disabled modal:', e);
            }
            return Promise.reject(error);
        }

        // Check for 403 Forbidden status with subscription required message
        if (error?.response?.status === 403 &&
            error?.response?.data?.message?.includes('подписка')) {
            // Show subscription required notification
            if (store && store.addNotification) {
                store.addNotification({
                    title: 'Требуется подписка',
                    message: _s(error?.response?.data?.message) || 'Для доступа к этой функции требуется активная подписка на программу лояльности Sagi.',
                    type: "warning",
                    insert: "top",
                    container: "top-right",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 7000,
                        onScreen: true
                    }
                });
            }
            // Redirect to offers page if not already there
            if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/offers')) {
                setTimeout(() => {
                    window.location.href = '/offers';
                }, 1000);
            }
            return Promise.reject(error);
        }

        const errorTitle = _s(error?.response?.data?.error || error?.response?.data?.message)
        const errorMessage = _s(error?.response?.data?.instructions || '')
        const statusCode = error?.response?.status

        // Skip showing error notification for award endpoint
        // For client errors (4xx), only show notification if there's an actual error message
        // For server errors (5xx), show notification with fallback to "Сервер не отвечает!"
        if (!isAwardEndpoint && !isSilentProbe && store && store.addNotification) {
            // Only show notification if:
            // - It's a server error (5xx), OR
            // - It's a client error (4xx) with an actual error message
            const shouldShowNotification = statusCode >= 500 || (statusCode >= 400 && errorTitle);

            if (shouldShowNotification) {
                store.addNotification({
                    title: `${errorTitle || i18n.t('Notify.notRespond')}`,
                    message: errorMessage ? errorMessage : 'Ошибка',
                    type: "danger",
                    insert: "top",
                    container: "top-right",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 5000,
                        onScreen: true
                    }
                });
            }
        }

        // Catch all 401 errors except "Token is expired" (handled by refresh below)
        if (error?.response?.status === 401 &&
            error?.response?.data?.message !== 'Token is expired'
        ) {
            removeToken();
            window.location.href = '/auth';
            return Promise.reject(error);
        }

        if (error?.response?.data?.message === 'Token is expired' && !error.config._retry) {
            try {
                error.config._retry = true;

                const { data } = await refreshToken();

                cookies.save(COOKIES.TOKEN, data.access_token, {path: '/'});
                token = data.access_token;
                return axiosInstance(error.config);
            } catch (e) {
                removeToken();
                window.history.go();
            }
        }

        // Sanitize error response data before rejecting
        if (error?.response?.data) {
            if (error.response.data.error) error.response.data.error = _s(error.response.data.error);
            if (error.response.data.message) error.response.data.message = _s(error.response.data.message);
            if (error.response.data.instructions) error.response.data.instructions = _s(error.response.data.instructions);
        }

        return Promise.reject(error);
    });

    return axiosInstance;
};

const instanceV2 = (authorization = true, url = null, token = null) => {
    const axiosInstance = axios.create({
        baseURL: url || API_URL_V2,
        transformResponse: [function (data) {
            // Handle empty or invalid responses
            if (!data || data === '') {
                return data;
            }

            try {
                return JsonBigString.parse(data);
            } catch (e) {
                console.error('Failed to parse response as JSON:', e);
                return data;
            }
        }],
    });

    if (authorization && !token) token = cookies.load(COOKIES.TOKEN);

    axiosInstance.interceptors.request.use(
      (config) => {
          config.headers.Authorization = `Bearer ${token}`;
          return config;
      },
      (error) => Promise.reject(error)
    );

    axiosInstance.interceptors.response.use((response) => {
        return response
    }, async function (error) {
        if (error?.message === APP_MESSAGE.API_CANCELLED) return Promise.reject(APP_MESSAGE.API_CANCELLED)

        // Check for specific 423 Locked status with FEATURE_DISABLED code
        // Skip showing modal for award endpoint
        const isAwardEndpoint = error?.config?.url?.endsWith('/award');

        // Probes whose callers already render the failure inline — a toast here is pure noise
        const isSilentProbe = ['/whatsapp/status', '/whatsapp/waba-status', '/configuration/logs']
            .some(e => error?.config?.url?.endsWith(e));
        if (error?.response?.status === 423 &&
            error?.response?.data?.code === 'FEATURE_DISABLED' &&
            !isAwardEndpoint) {
            // Import and trigger the feature disabled modal
            try {
                const { triggerFeatureDisabledModal } = await import('./utils/featureDisabledHandler');
                triggerFeatureDisabledModal();
            } catch (e) {
                console.error('Error triggering feature disabled modal:', e);
            }
            return Promise.reject(error);
        }

        // Check for 403 Forbidden status with subscription required message
        if (error?.response?.status === 403 &&
            error?.response?.data?.message?.includes('подписка')) {
            // Show subscription required notification
            if (store && store.addNotification) {
                store.addNotification({
                    title: 'Требуется подписка',
                    message: _s(error?.response?.data?.message) || 'Для доступа к этой функции требуется активная подписка на программу лояльности Sagi.',
                    type: "warning",
                    insert: "top",
                    container: "top-right",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 7000,
                        onScreen: true
                    }
                });
            }
            // Redirect to offers page if not already there
            if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/offers')) {
                setTimeout(() => {
                    window.location.href = '/offers';
                }, 1000);
            }
            return Promise.reject(error);
        }

        const errorTitle = _s(error?.response?.data?.error || error?.response?.data?.message)
        const errorMessage = _s(error?.response?.data?.instructions || '')
        const statusCode = error?.response?.status

        // Skip showing error notification for award endpoint
        // For client errors (4xx), only show notification if there's an actual error message
        // For server errors (5xx), show notification with fallback to "Сервер не отвечает!"
        if (!isAwardEndpoint && !isSilentProbe && store && store.addNotification) {
            // Only show notification if:
            // - It's a server error (5xx), OR
            // - It's a client error (4xx) with an actual error message
            const shouldShowNotification = statusCode >= 500 || (statusCode >= 400 && errorTitle);

            if (shouldShowNotification) {
                store.addNotification({
                    title: `${errorTitle || i18n.t('Notify.notRespond')}`,
                    message: errorMessage ? errorMessage : 'Ошибка',
                    type: "danger",
                    insert: "top",
                    container: "top-right",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 5000,
                        onScreen: true
                    }
                });
            }
        }

        // Catch all 401 errors except "Token is expired" (handled by refresh below)
        if (error?.response?.status === 401 &&
            error?.response?.data?.message !== 'Token is expired'
        ) {
            removeToken();
            window.location.href = '/auth';
            return Promise.reject(error);
        }

        if (error?.response?.data?.message === 'Token is expired' && !error.config._retry) {
            try {
                error.config._retry = true;

                const { data } = await refreshToken();

                cookies.save(COOKIES.TOKEN, data.access_token, {path: '/'});
                token = data.access_token;
                return axiosInstance(error.config);
            } catch (e) {
                removeToken();
                window.history.go();
            }
        }

        // Sanitize error response data before rejecting
        if (error?.response?.data) {
            if (error.response.data.error) error.response.data.error = _s(error.response.data.error);
            if (error.response.data.message) error.response.data.message = _s(error.response.data.message);
            if (error.response.data.instructions) error.response.data.instructions = _s(error.response.data.instructions);
        }

        return Promise.reject(error);
    });

    return axiosInstance;
};

export const login = (body) => {
    return instance(false).post(`/auth/business`, body).then(response => response.data);
}

export const registerBusiness = (body) => {
    return instance(false).post(`/auth/business/register`, body).then(response => response.data);
}

export const refreshToken = () => {
    return instance().post('/auth/refresh_token').then(response => response.data);
}

export const askCodeForgetPassword = (body) => {
    return instance().post('/auth/business/password/forget', body).then(response => response.data);
}

export const confirmCodeForgetPassword = (body) => {
    return instance().post('/auth/business/password/forget-confirm', body).then(response => response.data);
}

export const  resetPasswordForgetPassword = (body) => {
    return instance().post('/auth/business/password/reset-forgotten-password', body).then(response => response.data);
}

export const  sendBonusForUser = (branchId, customerId, body) => {
    return instance().post(`/branches/${branchId}/customers/${customerId}/bonus`, body).then(response => response.data);
}

export const  getWhatsAppQRUrl = (branchId) => {
    return instance().get(`/branches/${branchId}/mailing/get-screenshot-url`).then(response => response.data);
}

export const  getWhatsAppAuthorizationCode = (branchId) => {
    return instance().get(`/branches/${branchId}/mailing/get-authorization-code`).then(response => response.data);
}

export const  getWhatsAppStatus = (branchId) => {
    return instance().get(`/branches/${branchId}/whatsapp/status`).then(response => response.data);
}

export const  sendBonusesToNewUser = (branchId, body) => {
    return instance().post(`/branches/${branchId}/customer/bonus`, body).then(response => response.data);
}

export const  exportBranchCustomers = (branchId, startDate, endDate) => {
    let newparams = ``;
    if (startDate) {
        if (newparams.length) {
            newparams += `from=${startDate}&`
        } else {
            newparams = `?from=${startDate}&`
        }
    }
    if (endDate) {
        if (newparams.length) {
            newparams += `to=${endDate}`
        } else {
            newparams = `?to=${endDate}`
        }
    }
    return fetch(`${API_URL}/branches/${branchId}/customer/generate/file${newparams}`, {
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
            Authorization: `Bearer ${cookies.load(COOKIES.TOKEN)}`
        },
        method: 'GET'
    }).then(async (response) => {
        const blob = await response.blob();
        return { blob };
    });
}

export const  exportBranchCustomersExcel = (branchId) => {
    return fetch(`${API_URL}/branches/${branchId}/customers/uploaded/xlsx`, {
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
            Authorization: `Bearer ${cookies.load(COOKIES.TOKEN)}`
        },
        method: 'GET'
    }).then(async (response) => {
        const blob = await response.blob();
        let filename = 'customers.xlsx';
        
        // Try to extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '');
                filename = decodeURIComponent(filename);
            }
        }
        
        // Ensure the file has .xlsx extension
        if (!filename.toLowerCase().endsWith('.xlsx')) {
            filename = filename.replace(/\.[^/.]+$/, '') + '.xlsx';
        }
        
        if ('msSaveOrOpenBlob' in window.navigator) {
            navigator.msSaveBlob(blob, filename);
        } else {
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.href = window.URL.createObjectURL(blob);
            a.download = filename;
            a.target = '_blank';
            a.click();
            a.remove();
            // Clean up the object URL
            window.URL.revokeObjectURL(a.href);
        }

        return {};
    })
}

export const  exportBranchesTransactionsListExcel = (branchId, params) => {
    let newparams = ``;
    if (params.query) {
        newparams = `?query=${params.query}&`
    }
    if (params.from) {
        if (newparams.length) {
            newparams += `from=${params.from}&`
        } else {
            newparams = `?from=${params.from}&`
        }
    }
    if (params.to) {
        if (newparams.length) {
            newparams += `to=${params.to}`
        } else {
            newparams = `?to=${params.to}`
        }
    }
    return fetch(`${API_URL}/branches/${branchId}/transactions/list/excel${newparams}`, {
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/vnd.ms-excel;charset=UTF-8',
            Authorization: `Bearer ${cookies.load(COOKIES.TOKEN)}`
        },
        method: 'GET'
    }).then((response) => {
        response.blob().then((blob) => {
            const filename = decodeURI(response.headers.get('Content-Disposition')?.split('filename=')[1] || '')
            if ('msSaveOrOpenBlob' in window.navigator) {
                navigator.msSaveBlob(blob, filename)
            } else {
                const a = document.createElement('a')
                document.body.appendChild(a)
                a.href = window.URL.createObjectURL(blob)
                a.download = filename +'file.xlsx'
                a.target = '_blank'
                a.click()
                a.remove()
            }
        });

        return {};
    })
}

export default instance;
export { instanceV2 };